import React, { useEffect, useRef, useState } from "react";
import {
  ai_feedback_logo,
  ai_feedback_image,
} from "../../assets/images/images";
import "./AIFeedbackPerformance.css";
import { wsURL } from "../../api/api";
import { useAuth } from "../../features/auth/AuthContext";
import { useLocation } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { arrow_outward } from "../../assets/images/images";
import dayjs from "dayjs";
import useWebSocketFeedback from "../../hooks/useWebsocketFeedback";
import { useTabSwitchFeedback } from "../../hooks/useTabSwitchFeedback";

import {
  createOverallPerformancePayload,
  createEventsOverviewPayload,
  createSingleOverallFeedbackPayload,
  createSingleEventFeedbackPayload,
} from "../../utils/teacherPayloadUtils";
import {
  getTeacherDotClickFeedback,
  getStudentDotClickFeedback,
} from "../../api/getScoreFeedback";
import LoadingComponent from "../LoadingComponent/LoadingComponent";
import { resetCourseList } from "../../state/reducers/getAllCoursesByTeacherIdReducer";
import StandardizedAIFeedback from "./StandardizedAIFeedback";
import { resetPointData } from "../../state/reducers/getAiFeedbackPointByTeacherReducer";
import LoadingMessages from "./LoadingMessages";

const AIFeedbackPerformance = ({
  dot = null,
  setdot,
  value,
  selectedCourseData,
  selectedStudentData,
  periodTab,
  year,
  month,
  startdate,
  enddate,
  month_year,
  setValue,
  selectstudent,
  isAIGenerating,
  setIsAIGenerating,
}) => {
  const currentYear = new Date().getFullYear();

  // Helper function to extract month and year from dot data
  const extractMonthAndYearFromDot = (dotData) => {
    if (!dotData || dotData.isPlaceholder) {
      return { month: month, year: year || new Date().getFullYear() };
    }

    // For non-placeholder dots, prioritize the month field (actual date) over originalPeriod
    // For placeholder dots, use originalPeriod as it contains the expected interval
    let periodToProcess;
    if (dotData?.isPlaceholder) {
      // For placeholders, use originalPeriod which contains the expected interval
      periodToProcess = dotData?.originalPeriod || dotData?.month;
    } else {
      // For real data dots, prioritize month field which contains the actual date
      periodToProcess = dotData?.month || dotData?.originalPeriod;
    }

    let extractedMonth = month; // default fallback
    let extractedYear = year || new Date().getFullYear(); // default fallback

    if (periodToProcess) {
      const periodParts = periodToProcess.split(" ");
      if (periodParts.length === 2) {
        // If it's "15 May" format, extract month name and convert to number
        const monthName = periodParts[1];
        const monthNames = [
          "Jan", "Feb", "Mar", "Apr", "May", "Jun",
          "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ];
        const monthIndex = monthNames.findIndex(name =>
          name.toLowerCase() === monthName.toLowerCase()
        );
        if (monthIndex !== -1) {
          extractedMonth = monthIndex + 1; // Convert to 1-12 format
        }
      } else if (periodParts.length === 1) {
        // If it's just a month (e.g., "May")
        const monthName = periodParts[0];
        const monthNames = [
          "Jan", "Feb", "Mar", "Apr", "May", "Jun",
          "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ];
        const monthIndex = monthNames.findIndex(name =>
          name.toLowerCase() === monthName.toLowerCase()
        );
        if (monthIndex !== -1) {
          extractedMonth = monthIndex + 1; // Convert to 1-12 format
        }
      }
    }

    return { month: extractedMonth, year: extractedYear };
  };

  // Helper function to convert month number to month name (MMM format)
  const getMonthName = (monthNumber) => {
    const months = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];
    const validMonth = parseInt(monthNumber, 10);
    if (isNaN(validMonth) || validMonth < 1 || validMonth > 12) {
      return "Jan";
    }
    return months[validMonth - 1];
  };

  // Helper function to get current date in DD-MM-YYYY format
  const getCurrentDateFormatted = () => {
    const today = new Date();
    const day = today.getDate().toString().padStart(2, '0');
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    const year = today.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Initialize multiple WebSocket instances for different purposes
  const overallFeedback = useWebSocketFeedback();
  const courseFeedback = useWebSocketFeedback();
  const studentFeedback = useWebSocketFeedback();
  const dotClickFeedback = useWebSocketFeedback();
  const eventOverviewFeedback = useWebSocketFeedback();

  const DATA = useSelector(
    (state) => state?.getOverallPerformanceScoreByTeacher?.students?.data || []
  );

  const OverallAvgScoreStudentside = DATA.overall_average;

  const selectedcoursebystudent = DATA?.course_breakdown?.map(
    (course) => course.course_id
  );

  const globalState = useSelector((state) => state.globalState);

  const { token } = useAuth();
  const location = useLocation();
  const dispatch = useDispatch();

  // Course-specific loading states
  const [courseLoadingStates, setCourseLoadingStates] = useState({});

  // Feedback state
  const [overallFeedbackText, setOverallFeedbackText] = useState(""); // Separate state for overall feedback
  const [eventOverviewFeedbackText, setEventOverviewFeedbackText] = useState(""); // Separate state for event overview feedback
  const [dotClickFeedbackText, setDotClickFeedbackText] = useState(""); // Separate state for dot click feedback
  const [isReceivingFeedback, setIsReceivingFeedback] = useState(false);
  const [courseFeedbackText, setCourseFeedbackText] = useState("");
  const [courseFeedbackData, setCourseFeedbackData] = useState({});
  const [courseFeedbackContent, setCourseFeedbackContent] = useState({});
  const [studentsAreaOfImprovement, setStudentsAreaOfImprovement] = useState(
    ""
  );
  const [assignmentTypeStruggled, setAssignmentTypeStruggled] = useState("");
  const [
    impactOfPracticeAssignments,
    setImpactOfPracticeAssignments,
  ] = useState("");
  const [impactOfAIAssistant, setImpactOfAIAssistant] = useState("");
  const [practiceAssignmentText, setPracticeAssignmentText] = useState("");
  const [courseAssignmentText, setCourseAssignmentText] = useState("");
  const [testPrepText, setTestPrepText] = useState("");
  const [currentFeedbackType, setCurrentFeedbackType] = useState(""); // Track current feedback type
  const [hasOverallFeedbackData, setHasOverallFeedbackData] = useState(false); // Track if overall feedback has data
  const [headerUpdateTrigger, setHeaderUpdateTrigger] = useState(0); // Force header updates

  const teacher_id = globalState?.teacher_details?.data?.teacher_id;
  const courseData = useSelector(
    (state) => state?.getEnrolledStudentList?.students || []
  );
  
  // Get selected course data from Redux store
  const selectedCourseDataFromRedux = useSelector(
    (state) => state?.selectedCourseData?.selectedCourseData || []
  );
  
  // Get available students from Redux store
  const availableStudentsFromRedux = useSelector(
    (state) => state?.selectedCourseData?.availableStudents || []
  );

  const role = useSelector((state) => state?.globalState?.role || "");
  const studentID = useSelector(
    (state) => state?.globalState?.student_details?.data?.student_id || ""
  );

  const dotClickedData = useSelector(
    (state) => state.getAifeedbackPointByTeacher
  );
  const dotClickedDataStatus = useSelector(
    (state) => state.getAifeedbackPointByTeacher?.status
  );
  const studentList = useSelector(
    (state) =>
      state.getOverallPerformanceScoreByTeacher?.students?.data
        ?.student_breakdown
  );

  const time_period = useSelector(
    (state) =>
      state.getOverallPerformanceScoreByTeacher?.students?.data?.time_period
  );
  const timePeriodString = String(time_period);
  // const selectedStudentDatas = studentList?.map(
  //   (student) => student.student_id
  // );

  const selectedStudentDatas = selectedStudentData?.length > 0 
  ? selectedStudentData 
  : studentList?.map(student => student.student_id);

  const dotCLickedCourseInformation = dotClickedData?.courses_analytics || [];
  const filterApplied = dotClickedData?.filters_applied || [];
  const timePeriodFilterApplied = dotClickedData?.filters_applied?.time_period;

  const [showInsights, setShowInsights] = useState(false);
  const [studentCourse, setStudentCourse] = useState(false);
  const [doteventStudent, setDoteventStudent] = useState(false);
  const [showButton, setShowButton] = useState(true);
  
  // Timeout ref to prevent indefinite disabled state
  const aiGenerationTimeoutRef = useRef(null);

  // Helper function to set up timeout for AI generation
  const setupAIGenerationTimeout = () => {
    // Clear any existing timeout
    if (aiGenerationTimeoutRef.current) {
      clearTimeout(aiGenerationTimeoutRef.current);
    }
    
    // Set a timeout to automatically clear the loading state after 2 minutes
    aiGenerationTimeoutRef.current = setTimeout(() => {
      setIsAIGenerating(false);
      setIsReceivingFeedback(false);
      setCourseLoadingStates({});
    }, 120000); // 2 minutes timeout
  };

  // Use selected course data from Redux instead of deriving from courseData
  const courses = selectedCourseDataFromRedux.length > 0 ? selectedCourseDataFromRedux : [];
  
  // Use available students from Redux if available, otherwise derive from courseData
  // const students = availableStudentsFromRedux.length > 0 
  //   ? availableStudentsFromRedux.map(student => student.student_id)
  //   : (() => {
  //       const derivedStudents = [];
  //       if (selectedCourseDataFromRedux.length === 0) {
  //         courseData.forEach((course) => {
  //           course.students.forEach((student) => {
  //             derivedStudents.push(student.student_id);
  //           });
  //         });
  //       }
  //       return derivedStudents;
  //     })();

  const students = selectedStudentData?.length > 0
  ? selectedStudentData
  : availableStudentsFromRedux.length > 0
    ? availableStudentsFromRedux.map(student => student.student_id)
    : (() => {
        const derivedStudents = [];
        if (selectedCourseDataFromRedux.length === 0) {
          courseData.forEach((course) => {
            course.students.forEach((student) => {
              derivedStudents.push(student.student_id);
            });
          });
        }
        return derivedStudents;
      })();

  // Initialize tab switch feedback hook after all required variables are declared
  const {
    isTabSwitching,
    handleTabSwitch,
    resetTabSwitchState,
    existingFeedback,
    clearExistingFeedback,
    fetchCourseFeedback,
    fetchDotClickFeedback,
  } = useTabSwitchFeedback({
    role,
    teacher_id,
    studentID,
    courses,
    selectedcoursebystudent,
    selectedStudentDatas,
    OverallAvgScoreStudentside,
    periodTab,
    year,
    month,
    startdate,
    enddate,
    month_year,
    currentYear,
    token,
  });

  // Track previous values to avoid unnecessary API calls
  const previousValueRef = useRef(value);
  const previousPeriodRef = useRef({
    periodTab,
    year,
    month,
    startdate,
    enddate,
    month_year,
    selectedCourseData,
    selectedStudentData,
    selectstudent,
  });

  // Helper function to create course-specific feedback parser
  const createCourseFeedbackParser = (courseId) => (cleanedData) => {
    // Only process if we have meaningful content and it looks like complete data
    if (!cleanedData || cleanedData.trim() === "" || 
        cleanedData.includes("Starting AI feedback generation") ||
        cleanedData.includes("Analyzing performance data") ||
        cleanedData.includes("Generating AI feedback") ||
        cleanedData.includes("Heartbeat connection alive") ||
        cleanedData.includes("message")) {
      return;
    }

    // Check if this looks like complete feedback data (has multiple sections)
    const hasMultipleSections = cleanedData.includes("**Feedback:**") && 
                               (cleanedData.includes("**Impact of Practice Assignments:**") || 
                                cleanedData.includes("**Impact of AI Assistant:**") ||
                                cleanedData.includes("**Recommendations:**"));

    if (!hasMultipleSections) {
      return; // Don't process incomplete data
    }

    const sections = {
      "**Feedback:**": "studentsAreaOfImprovement",
      "**Assignment Type Students Struggled In:**": "assignmentTypeStruggled",
      "**Impact of Practice Assignments:**": "impactOfPracticeAssignments",    
      "**Impact of AI Assistant:**": "impactOfAIAssistant",
      "**Recommendations:**": "recommendations",
    };

    // Process sections in order and remove each section as we process it
    let remainingText = cleanedData;
    const extractedSections = {};
    
    // Define sections in order of appearance
    const orderedSections = [
      { marker: "**Feedback:**", key: "studentsAreaOfImprovement" },
      { marker: "**Impact of Practice Assignments:**", key: "impactOfPracticeAssignments" },
      { marker: "**Impact of AI Assistant:**", key: "impactOfAIAssistant" },
      { marker: "**Assignment Type Students Struggled In:**", key: "assignmentTypeStruggled" },
      { marker: "**Recommendations:**", key: "recommendations" }
    ];
    
    orderedSections.forEach(({ marker, key }) => {
      if (remainingText.includes(marker)) {
        
        // Find the position of this marker
        const markerIndex = remainingText.indexOf(marker);
        
        // Find the next marker after this one
        const nextMarker = orderedSections
          .slice(orderedSections.findIndex(s => s.marker === marker) + 1)
          .find(s => remainingText.includes(s.marker));
        
        let content;
        if (nextMarker) {
          // Extract content between current marker and next marker
          const nextMarkerIndex = remainingText.indexOf(nextMarker.marker);
          content = remainingText
            .substring(markerIndex + marker.length, nextMarkerIndex)
            .trim();
        } else {
          // This is the last section, take everything after the marker
          content = remainingText
            .substring(markerIndex + marker.length)
            .trim();
        }
        
        // Clean up the content
        content = content
          .replace(/^\n+/, '') // Remove leading newlines
          .replace(/\n+$/, '') // Remove trailing newlines
          .trim();
        
        if (content) {
          extractedSections[key] = content;
          
          // Remove this section from remaining text to avoid reprocessing
          remainingText = remainingText.substring(0, markerIndex) + 
                         remainingText.substring(markerIndex + marker.length + content.length);
        }
      }
    });
    
    // Update state with all extracted sections
    if (Object.keys(extractedSections).length > 0) {
      setCourseFeedbackData((prev) => ({
        ...prev,
        [courseId]: {
          ...prev[courseId],
          ...extractedSections,
        },
      }));
    }

    setCourseFeedbackContent((prev) => ({
      ...prev,
      [courseId]: true,
    }));
  };

  // Helper function to create event insights parser
  // This parser handles the new websocket data format with structured sections:
  // - Key Insights (general feedback)
  // - Practice Assignment (specific feedback)
  // - Course Assignment (specific feedback) 
  // - Test Prep (specific feedback)
  // - Recommendations (actionable suggestions)
  const createEventInsightsParser = () => (cleanedData) => {
    
    // Clear previous data
    setStudentsAreaOfImprovement("");
    setPracticeAssignmentText("");
    setCourseAssignmentText("");
    setTestPrepText("");
    setAssignmentTypeStruggled("");
    
    // Parse the new format with clear section headers
    const sections = [
      {
        name: "Key Insights",
        setter: setStudentsAreaOfImprovement,
        patterns: [
          /\*\*Key Insights\*\*\s*\*\*Feedback\*\*:\s*/i,
          /Key Insights\s*Feedback:\s*/i,
          /\*\*Key Insights\*\*\s*/i,
        ]
      },
      {
        name: "Practice Assignment",
        setter: setPracticeAssignmentText,
        patterns: [
          /\*\*Practice Assignment\*\*\s*/i,
          /Practice Assignment\s*/i,
        ]
      },
      {
        name: "Course Assignment",
        setter: setCourseAssignmentText,
        patterns: [
          /\*\*Course Assignment\*\*\s*/i,
          /Course Assignment\s*/i,
        ]
      },
      {
        name: "Test Prep",
        setter: setTestPrepText,
        patterns: [
          /\*\*Test Prep\*\*\s*/i,
          /Test Prep\s*/i,
        ]
      },
      {
        name: "Recommendations",
        setter: setAssignmentTypeStruggled,
        patterns: [
          /\*\*Recommendations:\*\*\s*/i,
          /Recommendations:\s*/i,
        ]
      }
    ];

    let foundAnySection = false;

    // Parse each section
    sections.forEach((section, index) => {
      let sectionStart = -1;
      let sectionPattern = null;

      // Try to find the section using different patterns
      for (const pattern of section.patterns) {
        const match = cleanedData.match(pattern);
        if (match) {
          sectionStart = match.index + match[0].length;
          sectionPattern = pattern;
          break;
        }
      }

      if (sectionStart !== -1) {
        foundAnySection = true;
        let content = "";

        // Find the next section or end of data
        let sectionEnd = cleanedData.length;
        
        // Look for the next section
        for (let i = index + 1; i < sections.length; i++) {
          const nextSection = sections[i];
          for (const pattern of nextSection.patterns) {
            const nextMatch = cleanedData.substring(sectionStart).match(pattern);
            if (nextMatch && nextMatch.index < sectionEnd - sectionStart) {
              sectionEnd = sectionStart + nextMatch.index;
              break;
            }
          }
          if (sectionEnd < cleanedData.length) break;
        }

        // Extract content between current section and next section
        content = cleanedData.substring(sectionStart, sectionEnd).trim();

        // Clean up the content
        content = content
          .replace(/^\n+/, '') // Remove leading newlines
          .replace(/\n+$/, '') // Remove trailing newlines
          .replace(/\*\*/g, '') // Remove markdown bold markers
          .trim();

        if (content) {
          section.setter(content);
        }
      }
    });

    // If no sections were found, try to parse the entire content as general feedback
    if (!foundAnySection) {
      setStudentsAreaOfImprovement(cleanedData);
    }

    setShowInsights(true);
  };

  // Helper function to create overall feedback parser
  // This parser handles the overall feedback format with structured sections:
  // - Feedback (general feedback)
  // - Impact of Practice Assignments (specific feedback)
  // - Impact of AI Assistant (specific feedback)
  // - Assignment Type Students Struggled In (specific feedback)
  // - Recommendations (actionable suggestions)
  const createOverallFeedbackParser = () => (cleanedData) => {
    // Helper function to extract embedded sections
    const extractEmbeddedSections = (text) => {
      const embeddedSections = {};
      
      // Extract Assignment Type Students Struggled In section
      const assignmentTypePatterns = [
        /\*\*Assignment Type Students Struggled In:\*\*\s*/i,
        /Assignment Type Students Struggled In:\s*/i,
        /Assignment Type Students Struggled:\s*/i
      ];
      
      for (const pattern of assignmentTypePatterns) {
        const match = text.match(pattern);
        if (match) {
          const start = match.index + match[0].length;
          let end = text.length;
          
          // Look for Recommendations section after this
          const recommendationsMatch = text.substring(start).match(/\*\*Recommendations:\*\*\s*/i);
          if (recommendationsMatch) {
            end = start + recommendationsMatch.index;
          }
          
          const content = text.substring(start, end).trim();
          if (content) {
            embeddedSections.assignmentType = content;
            // Remove this section from the original text
            text = text.substring(0, match.index) + text.substring(end);
            break;
          }
        }
      }
      
      // Extract Recommendations section
      const recommendationsMatch = text.match(/\*\*Recommendations:\*\*\s*/i);
      if (recommendationsMatch) {
        const start = recommendationsMatch.index + recommendationsMatch[0].length;
        const content = text.substring(start).trim();
        if (content) {
          embeddedSections.recommendations = content;
          // Remove this section from the original text
          text = text.substring(0, recommendationsMatch.index);
        }
      }
      
      return { cleanedText: text, embeddedSections };
    };
    
    // Clear previous data
    setStudentsAreaOfImprovement("");
    setPracticeAssignmentText("");
    setCourseAssignmentText("");
    setTestPrepText("");
    setAssignmentTypeStruggled("");
    
    // Extract embedded sections first
    const { cleanedText, embeddedSections } = extractEmbeddedSections(cleanedData);
    
    // Set embedded sections if found
    if (embeddedSections.assignmentType) {
      setTestPrepText(embeddedSections.assignmentType);
    }
    
    if (embeddedSections.recommendations) {
      setAssignmentTypeStruggled(embeddedSections.recommendations);
    }
    
    // Parse the overall feedback format with clear section headers
    const sections = [
      {
        name: "Feedback",
        setter: setStudentsAreaOfImprovement,
        patterns: [
          /^Feedback:\s*/i,
          /\*\*Feedback:\*\*\s*/i,
        ]
      },
      {
        name: "Impact of Practice Assignments",
        setter: setPracticeAssignmentText,
        patterns: [
          /^Impact of Practice Assignments:\s*/i,
          /\*\*Impact of Practice Assignments:\*\*\s*/i,
        ]
      },
      {
        name: "Impact of AI Assistant",
        setter: setCourseAssignmentText,
        patterns: [
          /^Impact of AI Assistant:\s*/i,
          /\*\*Impact of AI Assistant:\*\*\s*/i,
        ]
      },
      {
        name: "Assignment Type Students Struggled In",
        setter: setTestPrepText,
        patterns: [
          /^Assignment Type Students Struggled In:\s*/i,
          /\*\*Assignment Type Students Struggled In:\*\*\s*/i,
          /^Assignment Type Students Struggled:\s*/i,
          /\*\*Assignment Type Students Struggled:\*\*\s*/i,
        ]
      },
      {
        name: "Recommendations",
        setter: setAssignmentTypeStruggled,
        patterns: [
          /^Recommendations:\s*/i,
          /\*\*Recommendations:\*\*\s*/i,
        ]
      }
    ];

    let foundAnySection = false;

    // First, handle special case for embedded sections
    // Look for "**Assignment Type Students Struggled In:**" pattern that might be embedded in other sections
    const assignmentTypeMatch = cleanedData.match(/\*\*Assignment Type Students Struggled In:\*\*\s*/i);
    if (assignmentTypeMatch) {
      const assignmentTypeStart = assignmentTypeMatch.index + assignmentTypeMatch[0].length;
      // Find the next section or end
      let assignmentTypeEnd = cleanedData.length;
      const recommendationsMatch = cleanedData.substring(assignmentTypeStart).match(/\*\*Recommendations:\*\*\s*/i);
      if (recommendationsMatch) {
        assignmentTypeEnd = assignmentTypeStart + recommendationsMatch.index;
      }
      const assignmentTypeContent = cleanedData.substring(assignmentTypeStart, assignmentTypeEnd).trim();
      
      if (assignmentTypeContent) {
        setTestPrepText(assignmentTypeContent);
        foundAnySection = true;
      }
    }

    // Look for "**Recommendations:**" pattern that might be embedded in other sections
    const recommendationsMatch = cleanedData.match(/\*\*Recommendations:\*\*\s*/i);
    if (recommendationsMatch) {
      const recommendationsStart = recommendationsMatch.index + recommendationsMatch[0].length;
      const recommendationsEnd = cleanedData.length;
      const recommendationsContent = cleanedData.substring(recommendationsStart, recommendationsEnd).trim();
      
      if (recommendationsContent) {
        setAssignmentTypeStruggled(recommendationsContent);
        foundAnySection = true;
      }
    }

    // Also look for "Assignment Type Students Struggled:" pattern (without "In")
    const assignmentTypeMatch2 = cleanedData.match(/Assignment Type Students Struggled:\s*/i);
    if (assignmentTypeMatch2 && !testPrepText) {
      const assignmentTypeStart = assignmentTypeMatch2.index + assignmentTypeMatch2[0].length;
      // Find the next section or end
      let assignmentTypeEnd = cleanedData.length;
      const recommendationsMatch = cleanedData.substring(assignmentTypeStart).match(/\*\*Recommendations:\*\*\s*/i);
      if (recommendationsMatch) {
        assignmentTypeEnd = assignmentTypeStart + recommendationsMatch.index;
      }
      const assignmentTypeContent = cleanedData.substring(assignmentTypeStart, assignmentTypeEnd).trim();
      
      if (assignmentTypeContent) {
        setTestPrepText(assignmentTypeContent);
        foundAnySection = true;
      }
    }

    // Also look for "Assignment Type Students Struggled In:" pattern (without markdown)
    const assignmentTypeMatch3 = cleanedData.match(/Assignment Type Students Struggled In:\s*/i);
    if (assignmentTypeMatch3 && !testPrepText) {
      const assignmentTypeStart = assignmentTypeMatch3.index + assignmentTypeMatch3[0].length;
      // Find the next section or end
      let assignmentTypeEnd = cleanedData.length;
      const recommendationsMatch = cleanedData.substring(assignmentTypeStart).match(/\*\*Recommendations:\*\*\s*/i);
      if (recommendationsMatch) {
        assignmentTypeEnd = assignmentTypeStart + recommendationsMatch.index;
      }
      const assignmentTypeContent = cleanedData.substring(assignmentTypeStart, assignmentTypeEnd).trim();
      
      if (assignmentTypeContent) {
        setTestPrepText(assignmentTypeContent);
        foundAnySection = true;
      }
    }

    // Parse each section
    sections.forEach((section, index) => {
      let sectionStart = -1;
      let sectionPattern = null;

      // Try to find the section using different patterns
      for (const pattern of section.patterns) {
        const match = cleanedText.match(pattern);
        if (match) {
          sectionStart = match.index + match[0].length;
          sectionPattern = pattern;
          break;
        }
      }

      if (sectionStart !== -1) {
        foundAnySection = true;
        let content = "";

        // Find the next section or end of data
        let sectionEnd = cleanedText.length;
        
        // Look for the next section, but skip if it's the Recommendations section we already handled
        for (let i = index + 1; i < sections.length; i++) {
          const nextSection = sections[i];
          
          // Skip sections if we already handled them
          if (nextSection.name === "Recommendations" && assignmentTypeStruggled) {
            continue;
          }
          if (nextSection.name === "Assignment Type Students Struggled In" && testPrepText) {
            continue;
          }
          
          for (const pattern of nextSection.patterns) {
            const nextMatch = cleanedText.substring(sectionStart).match(pattern);
            if (nextMatch && nextMatch.index < sectionEnd - sectionStart) {
              sectionEnd = sectionStart + nextMatch.index;
              break;
            }
          }
          if (sectionEnd < cleanedText.length) break;
        }

        // Extract content between current section and next section
        content = cleanedText.substring(sectionStart, sectionEnd).trim();

        // Clean up the content - remove status messages and extra formatting
        content = content
          .replace(/^\n+/, '') // Remove leading newlines
          .replace(/\n+$/, '') // Remove trailing newlines
          .replace(/\*\*/g, '') // Remove markdown bold markers
          .replace(/💓 Heartbeat - Connection alive/g, '') // Remove heartbeat messages
          .replace(/Heartbeat - Connection alive/g, '') // Remove heartbeat messages
          .replace(/\*\*Recommendations:\*\*\s*/i, '') // Remove embedded recommendations header
          .replace(/\*\*Assignment Type Students Struggled In:\*\*\s*/i, '') // Remove embedded assignment type header
          .replace(/Assignment Type Students Struggled In:\s*/i, '') // Remove embedded assignment type header (no markdown)
          .replace(/Assignment Type Students Struggled:\s*/i, '') // Remove embedded assignment type header (no "In")
          .trim();

        if (content) {
          section.setter(content);
        }
      }
    });

    // If no sections were found, try to parse the entire content as general feedback
    if (!foundAnySection) {
      setStudentsAreaOfImprovement(cleanedText);
    }

    setShowInsights(true);
  };

  // Helper function to create student feedback parser
  const createStudentFeedbackParser = () => (cleanedData) => {
    // Clear previous data first
    setStudentsAreaOfImprovement("");
    setAssignmentTypeStruggled("");
    setImpactOfAIAssistant("");
    
    const studentSections = [
      {
        markers: [
          "## **Key Insights**",
          "**Key Insights**",
          "Key Insights:",
          "## Key Insights",
        ],
        setter: setStudentsAreaOfImprovement,
        next: [
          "## **Recommendations**",
          "**Recommendations**",
          "Recommendations:",
          "## Recommendations",
        ],
      },
      {
        markers: [
          "## **Recommendations**",
          "**Recommendations**",
          "Recommendations:",
          "## Recommendations",
        ],
        setter: setAssignmentTypeStruggled,
        next: null,
      },
      {
        markers: ["**Feedback:**", "Feedback:", "**Feedback**"],
        setter: setStudentsAreaOfImprovement,
        next: [
          "**Low AI Assistant Used:**",
          "**Low AI Assistant Used:**",
          "Low AI Assistant used",
          "**Low AI Assistant used**",
          "**AI Assistant Usage:**",
          "AI Assistant Usage:",
        ],
      },
      {
        markers: [
          "**Low AI Assistant Used:**",
          "**Low AI Assistant Used:**",
          "Low AI Assistant used",
          "**Low AI Assistant used**",
          "**AI Assistant Usage:**",
          "AI Assistant Usage:",
        ],
        setter: setImpactOfAIAssistant,
        next: [
          "**Recommendations:**",
          "*Recommendations*",
          "**Recommendations**",
          "Recommendations:",
        ],
      },
      {
        markers: [
          "**Practice Assignment Activity:**",
          "Practice Assignment Activity:",
        ],
        setter: setStudentsAreaOfImprovement,
        next: ["**AI Assistant Usage:**", "AI Assistant Usage:"],
      },
    ];

    let foundAnySection = false;

    // First, check if the content contains "Recommendations:" and extract it properly
    if (cleanedData.includes("Recommendations:")) {
      const recommendationsMatch = cleanedData.match(
        /Recommendations:\s*([\s\S]*?)(?=\n\n|\n[A-Z]|$)/
      );
      if (recommendationsMatch) {
        setAssignmentTypeStruggled(recommendationsMatch[1].trim());
        foundAnySection = true;
      }
    }

    // Process sections in order to ensure proper parsing
    studentSections.forEach(({ markers, setter, next }, index) => {
      const foundMarker = markers.find((marker) =>
        cleanedData.includes(marker)
      );

      if (foundMarker) {
        foundAnySection = true;


        // Find the next marker to determine content boundaries
        let nextMarker = null;
        if (next) {
          nextMarker = next.find((n) => cleanedData.includes(n));
        }

        let content = "";
        if (nextMarker) {
          // Extract content between current marker and next marker
          const parts = cleanedData.split(foundMarker);
          if (parts.length > 1) {
            const remainingText = parts[1];
            const nextParts = remainingText.split(nextMarker);
            if (nextParts.length > 0) {
              content = nextParts[0].trim();
            }
          }
        } else {
          // If no next marker, take everything after this marker
          const parts = cleanedData.split(foundMarker);
          if (parts.length > 1) {
            content = parts[1].trim();
          }
        }

        // Clean up the content
        if (content) {
          content = content
            .replace(/^\n+/, '') // Remove leading newlines
            .replace(/\n+$/, '') // Remove trailing newlines
            .trim();



          // Don't set content if it contains "Recommendations:" and we already set it
          if (
            !(
              content.includes("Recommendations:") &&
              setter === setStudentsAreaOfImprovement
            )
          ) {
            setter(content);
          }
        }
      }
    });

    // If no sections were found, try to parse the entire content
    if (!foundAnySection) {
      // Set the entire content as feedback
      setStudentsAreaOfImprovement(cleanedData);
    }

    setStudentCourse(true);
  };

  // WebSocket handler functions using the custom hook
  const handleRefreshAI = () => {
    const url = role === "Teacher" 
      ? `${wsURL}ws/teacher/overall-student-avg-score-feedback/${token}/`
      : `${wsURL}ws/overall-student-ai-feedback/?token=${token}`;
    
    // Derive month_year from dot's data if a dot is clicked, otherwise use calendar selection
    let derivedMonthYear = month_year;
    if (dot && !dot.isPlaceholder && periodTab === "year") {
      // Extract month name from dot's period data
      // For non-placeholder dots, prioritize the month field (actual date) over originalPeriod
      // For placeholder dots, use originalPeriod as it contains the expected interval
      let periodToProcess;
      if (dot?.isPlaceholder) {
        // For placeholders, use originalPeriod which contains the expected interval
        periodToProcess = dot?.originalPeriod || dot?.month;
      } else {
        // For real data dots, prioritize month field which contains the actual date
        periodToProcess = dot?.month || dot?.originalPeriod;
      }
      
      let monthName = "Jan"; // default fallback
      const currentYear = new Date().getFullYear();
      const effectiveYear = year || currentYear;
      
      if (periodToProcess) {
        const periodParts = periodToProcess.split(" ");
        if (periodParts.length === 2) {
          // If it's "15 May" format, extract month name
          monthName = periodParts[1];
        } else if (periodParts.length === 1) {
          // If it's just a month (e.g., "May")
          monthName = periodParts[0];
        }
      }
      
      derivedMonthYear = `${monthName}-${effectiveYear}`;
    }
    
    let payload = createOverallPerformancePayload({
      teacher_id: role === "Teacher" ? teacher_id : undefined,
      student_id: role === "Student" ? studentID : undefined,
      selectedCourseData: role === "Teacher" ? courses : selectedcoursebystudent,
      selectedStudentData: role === "Teacher" ? selectedStudentDatas : [studentID],
      avg_score: OverallAvgScoreStudentside,
      periodTab: role === "Teacher" ? timePeriodString : periodTab,
      year: year || currentYear,
      month: month,
      startdate: startdate,
      enddate: enddate,
      month_year: derivedMonthYear,
    });    

    // For overall custom feedback, set default dates if not provided
    const effectiveTimePeriod = role === "Teacher" ? timePeriodString : periodTab;
    if (effectiveTimePeriod === "custom") {
      const currentDate = new Date();
      const weekAgo = new Date();
      weekAgo.setDate(currentDate.getDate() - 7);
      
      // Format dates as DD-MM-YYYY
      const formatDate = (date) => {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      };
      
      payload.start_date = startdate || formatDate(weekAgo);
      payload.end_date = enddate || formatDate(currentDate);
    }

    // Clear states appropriately
    setIsReceivingFeedback(true);
    setCurrentFeedbackType("overall");
    setHasOverallFeedbackData(false);
    setOverallFeedbackText(""); // Clear previous feedback
    setIsAIGenerating(true); // Set global loading state
    setupAIGenerationTimeout(); // Set up timeout protection

    overallFeedback.connectWebSocket({
      url,
      payload,
      shouldConnect: () => {
        return !overallFeedback.isConnected;
      },
      onError: () => {
        console.error("WebSocket error in overall feedback generation");
        setIsReceivingFeedback(false);
        setIsAIGenerating(false); // Clear global loading state on error
      },
                           onDataReceived: (cleanedData, rawData) => {
          
          // Filter out status messages and only show actual feedback content
          const isStatusMessage = 
            cleanedData?.includes("Starting AI feedback generation") ||
            cleanedData?.includes("Analyzing performance data") ||
            cleanedData?.includes("Generating AI feedback") ||
            cleanedData?.includes("Heartbeat connection alive") ||
            cleanedData?.includes("message") ||
            (rawData && typeof rawData === 'object' && rawData.message);
          
          if (isStatusMessage) {
            return; // Don't update the UI for status messages
          }
          
          // Check if the data is empty or contains no meaningful content
          if (cleanedData && cleanedData.trim() !== "" && cleanedData !== "No student performance data available for analysis") {
            setOverallFeedbackText(cleanedData);
            setHasOverallFeedbackData(true);
            
            // Parse the data as it comes in for live display
            if (role === "Student") {
              // Use student feedback parser for students
              createStudentFeedbackParser()(cleanedData);
            } else {
              // Use overall feedback parser for teachers
              createOverallFeedbackParser()(cleanedData);
            }
          } else {
            setOverallFeedbackText("");
            setHasOverallFeedbackData(false);
          }
          // Don't set isReceivingFeedback to false here - let onComplete handle it
        },
             onComplete: async (finalData) => {
         
         // Filter out status messages in onComplete as well
         const isStatusMessage = 
           finalData?.includes("Starting AI feedback generation") ||
           finalData?.includes("Analyzing performance data") ||
           finalData?.includes("Generating AI feedback") ||
           finalData?.includes("Heartbeat connection alive") ||
           finalData?.includes("message");
         
         if (isStatusMessage) {
           setIsReceivingFeedback(false);
           return;
         }
         
                   // Only update if we don't already have the data (to avoid overriding)
          if (finalData && finalData.trim() !== "" && finalData !== "No student performance data available for analysis") {
                         // Only set if it's different from what we already have
             if (overallFeedbackText !== finalData) {
               setOverallFeedbackText(finalData);
               setHasOverallFeedbackData(true);
             }
             
             // Parse the final data based on role
             if (role === "Student") {
               // Use student feedback parser for students
               createStudentFeedbackParser()(finalData);
             } else {
               // Use overall feedback parser for teachers
               createOverallFeedbackParser()(finalData);
             }
          } else {
            setOverallFeedbackText("");
            setHasOverallFeedbackData(false);
          }
          setIsReceivingFeedback(false);
          setIsAIGenerating(false); // Clear global loading state
       },
    });
  };

  const handleCourseRefreshAI = (individualCourse) => {
    const courseId = individualCourse?.course_id;
    if (courseLoadingStates[courseId]) return;

    // Set loading state for this specific course
    setCourseLoadingStates((prev) => ({ ...prev, [courseId]: true }));
    // Set global loading state to disable all other course buttons
    setIsAIGenerating(true);
    setupAIGenerationTimeout(); // Set up timeout protection

    const url = role === "Teacher" 
      ? `${wsURL}ws/teacher/all-student-avg-score-feedback/${token}/`
      : `${wsURL}ws/student-ai-feedback/?token=${token}`;
    
    // Derive month_year from dot's data if a dot is clicked, otherwise use calendar selection
    let derivedMonthYear = month_year;
    if (dot && !dot.isPlaceholder && periodTab === "year") {
      // Extract month name from dot's period data
      // For non-placeholder dots, prioritize the month field (actual date) over originalPeriod
      // For placeholder dots, use originalPeriod as it contains the expected interval
      let periodToProcess;
      if (dot?.isPlaceholder) {
        // For placeholders, use originalPeriod which contains the expected interval
        periodToProcess = dot?.originalPeriod || dot?.month;
      } else {
        // For real data dots, prioritize month field which contains the actual date
        periodToProcess = dot?.month || dot?.originalPeriod;
      }
      
      let monthName = "Jan"; // default fallback
      const currentYear = new Date().getFullYear();
      const effectiveYear = year || currentYear;
      
      if (periodToProcess) {
        const periodParts = periodToProcess.split(" ");
        if (periodParts.length === 2) {
          // If it's "15 May" format, extract month name
          monthName = periodParts[1];
        } else if (periodParts.length === 1) {
          // If it's just a month (e.g., "May")
          monthName = periodParts[0];
        }
      }
      
      derivedMonthYear = `${monthName}-${effectiveYear}`;
    }
    
    let payload = createSingleOverallFeedbackPayload({
      teacher_id: role === "Teacher" ? teacher_id : undefined,
      student_id: role === "Student" ? studentID : undefined,
      selectedCourseData: [courseId],
      selectedStudentData: role === "Teacher" ? students : [studentID],
      avg_score: OverallAvgScoreStudentside,
      periodTab: role === "Teacher" ? timePeriodFilterApplied : periodTab,
      year: year,
      month: month,
      startdate: startdate,
      enddate: enddate,
      month_year: derivedMonthYear,
      week_start_date: filterApplied?.week_start_date || startdate, // Prioritize dot's week_start_date
      daily_date: filterApplied?.daily_date || startdate, // Prioritize dot's daily_date
    });

    // Add time period specific fields
    const effectiveTimePeriod = role === "Teacher" ? timePeriodFilterApplied : periodTab;
    const effectiveFilterApplied = role === "Teacher" ? filterApplied : null;
    
    if (effectiveTimePeriod === "year") {
      payload.month_year = derivedMonthYear;
    } else if (effectiveTimePeriod === "month") {
      // Only override week_start_date if we have a valid value from filter
      if (effectiveFilterApplied?.week_start_date) {
        payload.week_start_date = effectiveFilterApplied.week_start_date;
      }
    } else if (effectiveTimePeriod === "week") {
      // Only override daily_date if we have a valid value from filter
      if (effectiveFilterApplied?.daily_date) {
        payload.daily_date = effectiveFilterApplied.daily_date;
      }
    } else if (effectiveTimePeriod === "custom") {
      // Remove start_date and end_date for custom tab - they should not be sent
      delete payload.start_date;
      delete payload.end_date;
      // Set daily_date for custom tab - prioritize dot's actual date over startdate
      let dotDate = null;
      if (dot && !dot.isPlaceholder) {
        // Calculate daily_date from dot's data - prioritize actualDate from EventOverViewGraph
        if (dot?.actualDate) {
          // Use the actualDate from EventOverViewGraph if available (highest priority)
          dotDate = dot.actualDate;
        } else if (dot?.date) {
          // If dot has a date object, format it
          const date = new Date(dot.date);
          dotDate = `${date.getDate().toString().padStart(2, '0')}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getFullYear()}`;
        } else if (dot?.originalDate) {
          // If dot has originalDate string, use it directly
          dotDate = dot.originalDate;
        } else if (dot?.submission_date) {
          // If dot has submission_date, use it
          dotDate = dot.submission_date;
        } else if (dot?.month) {
          // For real data dots, calculate from month field
          const periodParts = dot.month.split(" ");
          if (periodParts.length === 2) {
            // If it's "15 May" format
            const [day, monthName] = periodParts;
            const monthNumber = new Date(`${monthName} 1, 2021`).getMonth() + 1;
            const effectiveYear = year || new Date().getFullYear();
            dotDate = `${day.padStart(2, '0')}-${monthNumber.toString().padStart(2, '0')}-${effectiveYear}`;
          }
        }
      }
      
      // Format the daily_date to DD-MM-YYYY format
      const formatDailyDate = (dailyDate, month, year) => {
        if (!dailyDate) return dailyDate;
        
        // If already in DD-MM-YYYY format, return as is
        if (/^\d{2}-\d{2}-\d{4}$/.test(dailyDate)) {
          return dailyDate;
        }
        
        // If in "DD MMM" format (e.g., "28 Aug"), convert to DD-MM-YYYY
        const match = dailyDate.match(/^(\d{1,2})\s+([A-Za-z]{3})$/);
        if (match) {
          const day = match[1].padStart(2, '0');
          
          // If month and year are provided, use them
          if (month && year) {
            const monthStr = month.toString().padStart(2, '0');
            return `${day}-${monthStr}-${year}`;
          }
          
          // If month and year are not provided, try to get current month/year
          const currentDate = new Date();
          const currentMonth = currentDate.getMonth() + 1;
          const currentYear = currentDate.getFullYear();
          const monthStr = currentMonth.toString().padStart(2, '0');
          return `${day}-${monthStr}-${currentYear}`;
        }
        
        return dailyDate;
      };
      
      payload.daily_date = formatDailyDate(dotDate || startdate, month, year) || new Date().toISOString().split('T')[0];
    }

    courseFeedback.connectWebSocket({
      url,
      payload,
      onDataReceived: (cleanedData) => {
        // Filter out status messages for course feedback
        const isStatusMessage = 
          cleanedData?.includes("Starting AI feedback generation") ||
          cleanedData?.includes("Analyzing performance data") ||
          cleanedData?.includes("Generating AI feedback") ||
          cleanedData?.includes("Heartbeat connection alive") ||
          cleanedData?.includes("message");
        
        if (isStatusMessage) {
          return;
        }
        
        setCourseFeedbackText(cleanedData);
        // Don't parse on every data chunk - only parse complete data
        // The parser will be called in onComplete with the final data
      },
      onComplete: async (finalData) => {
        // Filter out status messages in onComplete for course feedback
        const isStatusMessage = 
          finalData?.includes("Starting AI feedback generation") ||
          finalData?.includes("Analyzing performance data") ||
          finalData?.includes("Generating AI feedback") ||
          finalData?.includes("Heartbeat connection alive") ||
          finalData?.includes("message");
        
        if (isStatusMessage) {
          setCourseLoadingStates((prev) => ({ ...prev, [courseId]: false }));
          return;
        }
        
        // Parse the complete feedback data
        if (finalData && finalData.trim() !== "") {
          createCourseFeedbackParser(courseId)(finalData);
        }
        
        let daily_date = null;
        const effectiveTimePeriod = role === "Teacher" ? timePeriodFilterApplied : periodTab;
        
        if (effectiveTimePeriod === "custom" && startdate) {
          // For custom tab, use startdate as daily_date
          daily_date = startdate;
        } else if (effectiveTimePeriod === "week") {
          daily_date = startdate || new Date().toISOString().split('T')[0];
        } else if (effectiveTimePeriod === "month") {
          daily_date = startdate || `01-${month || "01"}-${year || new Date().getFullYear()}`;
        } else if (effectiveTimePeriod === "year") {
          daily_date = startdate || `01-01-${year || new Date().getFullYear()}`;
        }

  
        setCourseLoadingStates((prev) => {
          const newState = { ...prev, [courseId]: false };
          // Check if any other course is still loading
          const hasOtherLoading = Object.values(newState).some(loading => loading);
          // Only clear global loading state if no other course is loading
          if (!hasOtherLoading) {
            setIsAIGenerating(false);
          }
          return newState;
        });
      },
    });
  };

  const handleRefreshAIStudent = () => {
    const url = `${wsURL}ws/overall-student-ai-feedback/?token=${token}`;
    
    // Derive month_year from dot's data if a dot is clicked, otherwise use calendar selection
    let derivedMonthYear = month_year;
    if (dot && !dot.isPlaceholder && periodTab === "year") {
      // Extract month name from dot's period data
      // For non-placeholder dots, prioritize the month field (actual date) over originalPeriod
      // For placeholder dots, use originalPeriod as it contains the expected interval
      let periodToProcess;
      if (dot?.isPlaceholder) {
        // For placeholders, use originalPeriod which contains the expected interval
        periodToProcess = dot?.originalPeriod || dot?.month;
      } else {
        // For real data dots, prioritize month field which contains the actual date
        periodToProcess = dot?.month || dot?.originalPeriod;
      }
      
      let monthName = "Jan"; // default fallback
      const currentYear = new Date().getFullYear();
      const effectiveYear = year || currentYear;
      
      if (periodToProcess) {
        const periodParts = periodToProcess.split(" ");
        if (periodParts.length === 2) {
          // If it's "15 May" format, extract month name
          monthName = periodParts[1];
        } else if (periodParts.length === 1) {
          // If it's just a month (e.g., "May")
          monthName = periodParts[0];
        }
      }
      
      derivedMonthYear = `${monthName}-${effectiveYear}`;
    }
    
    let payload = createOverallPerformancePayload({
      student_id: studentID, // Use student_id for students
      selectedCourseData: selectedcoursebystudent,
      selectedStudentData: [studentID],
      avg_score: OverallAvgScoreStudentside,
      periodTab: periodTab,
      year: year,
      month: month,
      startdate: startdate,
      enddate: enddate,
      month_year: derivedMonthYear,
    });

    if (periodTab === "custom") {
      // For overall custom feedback, set default dates if not provided
      const currentDate = new Date();
      const weekAgo = new Date();
      weekAgo.setDate(currentDate.getDate() - 7);
      
      // Format dates as DD-MM-YYYY
      const formatDate = (date) => {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      };
      
      payload.start_date = startdate || formatDate(weekAgo);
      payload.end_date = enddate || formatDate(currentDate);
    }

    // Clear states appropriately
    setIsReceivingFeedback(true);
    setCurrentFeedbackType("overall");
    setHasOverallFeedbackData(false);
    setOverallFeedbackText(""); // Clear previous feedback
    setIsAIGenerating(true); // Set global loading state
    setupAIGenerationTimeout(); // Set up timeout protection

    studentFeedback.connectWebSocket({
      url,
      payload,
      shouldConnect: () =>
        !studentFeedback.isConnected &&
        (!overallFeedbackText || overallFeedbackText.trim() === ""),
      onError: () => {
        console.error("WebSocket error in student feedback generation");
        setIsReceivingFeedback(false);
        setIsAIGenerating(false); // Clear global loading state on error
      },
             onDataReceived: (cleanedData) => {
         // Filter out status messages for student feedback
         const isStatusMessage = 
           cleanedData?.includes("Starting AI feedback generation") ||
           cleanedData?.includes("Analyzing performance data") ||
           cleanedData?.includes("Generating AI feedback") ||
           cleanedData?.includes("Heartbeat connection alive") ||
           cleanedData?.includes("message");
         
         if (isStatusMessage) {
           return;
         }
         
         setOverallFeedbackText(cleanedData);
         setHasOverallFeedbackData(true);
         
         // Parse the student feedback to separate sections
         createStudentFeedbackParser()(cleanedData);
       },
             onComplete: async (finalData) => {
         // Filter out status messages in onComplete for student feedback
         const isStatusMessage = 
           finalData?.includes("Starting AI feedback generation") ||
           finalData?.includes("Analyzing performance data") ||
           finalData?.includes("Generating AI feedback") ||
           finalData?.includes("Heartbeat connection alive") ||
           finalData?.includes("message");
         
         if (isStatusMessage) {
           setIsReceivingFeedback(false);
           return;
         }
         
         setOverallFeedbackText(finalData);
         setHasOverallFeedbackData(true);
         
                 // Parse the final student feedback to separate sections
        createStudentFeedbackParser()(finalData);
        
        setIsReceivingFeedback(false);
        setIsAIGenerating(false); // Clear global loading state
      },
    });
  };

  const handleCourseRefreshAIStudent = (individualCourse) => {
    const courseId = individualCourse?.course_id;
    if (courseLoadingStates[courseId]) return;

    // Set loading state for this specific course
    setCourseLoadingStates((prev) => ({ ...prev, [courseId]: true }));
    // Set global loading state to disable all other course buttons
    setIsAIGenerating(true);
    setupAIGenerationTimeout(); // Set up timeout protection

    const url = `${wsURL}ws/student-ai-feedback/?token=${token}`;
    
    // Derive month_year from dot's data if a dot is clicked, otherwise use calendar selection
    let derivedMonthYear = month_year;
    if (dot && !dot.isPlaceholder && periodTab === "year") {
      // Extract month name from dot's period data
      // For non-placeholder dots, prioritize the month field (actual date) over originalPeriod
      // For placeholder dots, use originalPeriod as it contains the expected interval
      let periodToProcess;
      if (dot?.isPlaceholder) {
        // For placeholders, use originalPeriod which contains the expected interval
        periodToProcess = dot?.originalPeriod || dot?.month;
      } else {
        // For real data dots, prioritize month field which contains the actual date
        periodToProcess = dot?.month || dot?.originalPeriod;
      }
      
      let monthName = "Jan"; // default fallback
      const currentYear = new Date().getFullYear();
      const effectiveYear = year || currentYear;
      
      if (periodToProcess) {
        const periodParts = periodToProcess.split(" ");
        if (periodParts.length === 2) {
          // If it's "15 May" format, extract month name
          monthName = periodParts[1];
        } else if (periodParts.length === 1) {
          // If it's just a month (e.g., "May")
          monthName = periodParts[0];
        }
      }
      
      derivedMonthYear = `${monthName}-${effectiveYear}`;
    }
    
    let payload = createSingleOverallFeedbackPayload({
      student_id: studentID, // Use student_id for students
      selectedCourseData: [courseId],
      selectedStudentData: [studentID],
      avg_score: OverallAvgScoreStudentside,
      periodTab: periodTab,
      year: year,
      month: month,
      startdate: startdate,
      enddate: enddate,
      month_year: derivedMonthYear,
      week_start_date: filterApplied?.week_start_date || startdate, // Prioritize dot's week_start_date
      daily_date: filterApplied?.daily_date || startdate, // Prioritize dot's daily_date
    });

    // Add time period specific fields for students
    if (periodTab === "year") {
      payload.month_year = derivedMonthYear;
    } else if (periodTab === "month") {
      // Only override week_start_date if we have a valid startdate
      if (startdate) {
        payload.week_start_date = startdate;
      }
    } else if (periodTab === "week") {
      // Only override daily_date if we have a valid startdate
      if (startdate) {
        payload.daily_date = startdate;
      }
    } else if (periodTab === "custom") {
      // Remove start_date and end_date for custom tab - they should not be sent
      delete payload.start_date;
      delete payload.end_date;
      // Set daily_date for custom tab - prioritize dot's actual date over startdate
      let dotDate = null;
      if (dot && !dot.isPlaceholder) {
        // Calculate daily_date from dot's data - prioritize actualDate from EventOverViewGraph
        if (dot?.actualDate) {
          // Use the actualDate from EventOverViewGraph if available (highest priority)
          dotDate = dot.actualDate;
        } else if (dot?.date) {
          // If dot has a date object, format it
          const date = new Date(dot.date);
          dotDate = `${date.getDate().toString().padStart(2, '0')}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getFullYear()}`;
        } else if (dot?.originalDate) {
          // If dot has originalDate string, use it directly
          dotDate = dot.originalDate;
        } else if (dot?.submission_date) {
          // If dot has submission_date, use it
          dotDate = dot.submission_date;
        } else if (dot?.month) {
          // For real data dots, calculate from month field
          const periodParts = dot.month.split(" ");
          if (periodParts.length === 2) {
            // If it's "15 May" format
            const [day, monthName] = periodParts;
            const monthNumber = new Date(`${monthName} 1, 2021`).getMonth() + 1;
            const effectiveYear = year || new Date().getFullYear();
            dotDate = `${day.padStart(2, '0')}-${monthNumber.toString().padStart(2, '0')}-${effectiveYear}`;
          }
        }
      }
      
      // Format the daily_date to DD-MM-YYYY format
      const formatDailyDate = (dailyDate, month, year) => {
        if (!dailyDate) return dailyDate;
        
        // If already in DD-MM-YYYY format, return as is
        if (/^\d{2}-\d{2}-\d{4}$/.test(dailyDate)) {
          return dailyDate;
        }
        
        // If in "DD MMM" format (e.g., "28 Aug"), convert to DD-MM-YYYY
        const match = dailyDate.match(/^(\d{1,2})\s+([A-Za-z]{3})$/);
        if (match) {
          const day = match[1].padStart(2, '0');
          
          // If month and year are provided, use them
          if (month && year) {
            const monthStr = month.toString().padStart(2, '0');
            return `${day}-${monthStr}-${year}`;
          }
          
          // If month and year are not provided, try to get current month/year
          const currentDate = new Date();
          const currentMonth = currentDate.getMonth() + 1;
          const currentYear = currentDate.getFullYear();
          const monthStr = currentMonth.toString().padStart(2, '0');
          return `${day}-${monthStr}-${currentYear}`;
        }
        
        return dailyDate;
      };
      
      payload.daily_date = formatDailyDate(dotDate || startdate, month, year) || new Date().toISOString().split('T')[0];
    }

    courseFeedback.connectWebSocket({
      url,
      payload,
      onDataReceived: (cleanedData) => {
        // Filter out status messages for course feedback
        const isStatusMessage = 
          cleanedData?.includes("Starting AI feedback generation") ||
          cleanedData?.includes("Analyzing performance data") ||
          cleanedData?.includes("Generating AI feedback") ||
          cleanedData?.includes("Heartbeat connection alive") ||
          cleanedData?.includes("message");
        
        if (isStatusMessage) {
          return;
        }
        
        setCourseFeedbackText(cleanedData);
        // Don't parse on every data chunk - only parse complete data
        // The parser will be called in onComplete with the final data
      },
      onComplete: async (finalData) => {
        // Filter out status messages in onComplete for course feedback
        const isStatusMessage = 
          finalData?.includes("Starting AI feedback generation") ||
          finalData?.includes("Analyzing performance data") ||
          finalData?.includes("Generating AI feedback") ||
          finalData?.includes("Heartbeat connection alive") ||
          finalData?.includes("message");
        
        if (isStatusMessage) {
          setCourseLoadingStates((prev) => ({ ...prev, [courseId]: false }));
          return;
        }
        
        // Parse the complete feedback data
        if (finalData && finalData.trim() !== "") {
          createCourseFeedbackParser(courseId)(finalData);
        }
        
        setCourseLoadingStates((prev) => {
          const newState = { ...prev, [courseId]: false };
          // Check if any other course is still loading
          const hasOtherLoading = Object.values(newState).some(loading => loading);
          // Only clear global loading state if no other course is loading
          if (!hasOtherLoading) {
            setIsAIGenerating(false);
          }
          return newState;
        });
      },
    });
  };


  const handleRefreshAIOverallTeacherSide = () => {
    const url =
      role === "Student"
        ? `${wsURL}ws/overall-student-ai-feedback/?token=${token}`
        : `${wsURL}ws/teacher/overall-student-avg-score-feedback/${token}/`;

    // Derive month_year from dot's data if a dot is clicked, otherwise use calendar selection
    let derivedMonthYear = month_year;
    if (dot && !dot.isPlaceholder && periodTab === "year") {
      // Extract month name from dot's period data
      // For non-placeholder dots, prioritize the month field (actual date) over originalPeriod
      // For placeholder dots, use originalPeriod as it contains the expected interval
      let periodToProcess;
      if (dot?.isPlaceholder) {
        // For placeholders, use originalPeriod which contains the expected interval
        periodToProcess = dot?.originalPeriod || dot?.month;
      } else {
        // For real data dots, prioritize month field which contains the actual date
        periodToProcess = dot?.month || dot?.originalPeriod;
      }
      
      let monthName = "Jan"; // default fallback
      const currentYear = new Date().getFullYear();
      const effectiveYear = year || currentYear;
      
      if (periodToProcess) {
        const periodParts = periodToProcess.split(" ");
        if (periodParts.length === 2) {
          // If it's "15 May" format, extract month name
          monthName = periodParts[1];
        } else if (periodParts.length === 1) {
          // If it's just a month (e.g., "May")
          monthName = periodParts[0];
        }
      }
      
      derivedMonthYear = `${monthName}-${effectiveYear}`;
    }

    let payload = createEventsOverviewPayload({
      teacher_id: role === "Teacher" ? teacher_id : undefined,
      student_id: role === "Student" ? studentID : undefined,
      selectedCourseData:
        role === "Teacher" ? courses : selectedcoursebystudent,
      selectedStudentData:
        role === "Teacher" ? selectedStudentData : [studentID],        //s hata diya hu selectedStudentDatas mai se now working fine bug
      avg_score: OverallAvgScoreStudentside,
      periodTab: periodTab,
      year: year || new Date().getFullYear(),
      month: month,
      startdate: startdate,
      enddate: enddate,
      month_year: derivedMonthYear,
    });

    // For overall custom feedback, set default dates if not provided
    if (periodTab === "custom") {
      const currentDate = new Date();
      const weekAgo = new Date();
      weekAgo.setDate(currentDate.getDate() - 7);
      
      // Format dates as DD-MM-YYYY
      const formatDate = (date) => {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      };
      
      payload.start_date = startdate || formatDate(weekAgo);
      payload.end_date = enddate || formatDate(currentDate);
    }

        setCurrentFeedbackType("event_overview");
    setEventOverviewFeedbackText(""); // Clear previous feedback
    setIsReceivingFeedback(true);
    setIsAIGenerating(true); // Set global loading state
    setupAIGenerationTimeout(); // Set up timeout protection

    eventOverviewFeedback.connectWebSocket({
      url,
      payload,
      shouldConnect: () =>
        !eventOverviewFeedback.isConnected &&
        (!eventOverviewFeedbackText || eventOverviewFeedbackText.trim() === ""),
      onError: () => {
        console.error("WebSocket error in event overview feedback generation");
        setIsReceivingFeedback(false);
        setIsAIGenerating(false); // Clear global loading state on error
      },
      onDataReceived: (cleanedData) => {
        
        // Filter out status messages for event overview feedback
        const isStatusMessage = 
          cleanedData?.includes("Starting AI feedback generation") ||
          cleanedData?.includes("Analyzing performance data") ||
          cleanedData?.includes("Generating AI feedback") ||
          cleanedData?.includes("Heartbeat connection alive") ||
          cleanedData?.includes("message");
        
        if (isStatusMessage) {
          return;
        }
        
                 // Check if the data is empty or contains no meaningful content
         if (cleanedData && cleanedData.trim() !== "" && cleanedData !== "No student performance data available for analysis") {
           setEventOverviewFeedbackText(cleanedData);
           
           // Parse the data as it comes in for live display
           createEventInsightsParser()(cleanedData);
         } else {
           setEventOverviewFeedbackText("");
         }
         // Don't set isReceivingFeedback to false here - let onComplete handle it
      },
             onComplete: async (finalData) => {
        
        // Filter out status messages in onComplete for event overview feedback
        const isStatusMessage = 
          finalData?.includes("Starting AI feedback generation") ||
          finalData?.includes("Analyzing performance data") ||
          finalData?.includes("Generating AI feedback") ||
          finalData?.includes("Heartbeat connection alive") ||
          finalData?.includes("message");
        
        if (isStatusMessage) {
          setIsReceivingFeedback(false);
          return;
        }
        
        // Only update if we don't already have the data (to avoid overriding)
        if (finalData && finalData.trim() !== "" && finalData !== "No student performance data available for analysis") {
          // Only set if it's different from what we already have
          if (eventOverviewFeedbackText !== finalData) {
            setEventOverviewFeedbackText(finalData);
          }
        } else {
          setEventOverviewFeedbackText("");
        }
        

        
        setIsReceivingFeedback(false);
        setIsAIGenerating(false); // Clear global loading state
      },
    });
  };

  const handleDotClickAI = () => {
    if (!dot || dot.isPlaceholder) return;

    // Additional check to prevent multiple connections
    if (dotClickFeedback.isConnected || dotClickFeedback.loading) {
      return;
    }

    setIsAIGenerating(true); // Set global loading state

  

    const generateDotClickFeedback = (dot) => {
  let daily_date = null;
    const currentYear = new Date().getFullYear();
    const effectiveYear = year || currentYear;
    
    // Check if dot has a direct date field first - prioritize dot's actual date over startdate
    if (dot?.actualDate) {
      // Use the actualDate from EventOverViewGraph if available (highest priority)
      daily_date = dot.actualDate;
    } else if (dot?.date) {
      // If dot has a date object, format it
      const date = new Date(dot.date);
      daily_date = `${date.getDate().toString().padStart(2, '0')}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getFullYear()}`;
    } else if (dot?.originalDate) {
      // If dot has originalDate string, use it directly
      daily_date = dot.originalDate;
    } else if (dot?.submission_date) {
      // If dot has submission_date, use it
      daily_date = dot.submission_date;
    } else if (periodTab === "custom" && startdate) {
      // For custom tab, only use startdate as fallback if no dot date is available
      daily_date = startdate;
    } else {
      // Fallback to calculating from period
      // For non-placeholder dots, prioritize the month field (actual date) over originalPeriod
      // For placeholder dots, use originalPeriod as it contains the expected interval
      let periodToProcess;
      if (dot?.isPlaceholder) {
        // For placeholders, use originalPeriod which contains the expected interval
        periodToProcess = dot?.originalPeriod || dot?.month;
      } else {
        // For real data dots, prioritize month field which contains the actual date
        periodToProcess = dot?.month || dot?.originalPeriod;
      }
      
      if (periodToProcess) {
        const periodParts = periodToProcess.split(" ");
        
        if (periodParts.length === 2) {
          // If it's "15 May" format
          const [day, monthName] = periodParts;
          const monthNumber = new Date(`${monthName} 1, 2021`).getMonth() + 1;
          daily_date = `${day.padStart(2, '0')}-${monthNumber.toString().padStart(2, '0')}-${effectiveYear}`;
        } else if (periodParts.length === 1) {
          // If it's just a month (e.g., "May")
          const monthName = periodParts[0];
          const monthNumber = new Date(`${monthName} 1, 2021`).getMonth() + 1;
          daily_date = `01-${monthNumber.toString().padStart(2, '0')}-${effectiveYear}`;
        }
      }
    }


    const url =
      role === "Student"
        ? `${wsURL}ws/student-ai-feedback/?token=${token}`
        : `${wsURL}ws/teacher/all-student-avg-score-feedback/${token}/`;

    // Extract month and year from dot's data
    const { month: derivedMonth, year: derivedYear } = extractMonthAndYearFromDot(dot);
    
    // Prepare payload with proper time period handling
    const payloadData = {
      teacher_id: role === "Teacher" ? parseInt(teacher_id) : undefined,
      student_id: role === "Student" ? studentID : undefined,
      selectedCourseData: [parseInt(dot?.course_id)],
      selectedStudentData:
        role === "Teacher" ? [parseInt(dot?.student_id, 10)] : [studentID],
      avg_score: dot?.score || 0,
      periodTab: periodTab,
      year: derivedYear,
      month: derivedMonth,
      daily_date: daily_date, // Pass the calculated daily_date
      // For custom time period, don't include startdate and enddate for dot feedback
      // These should only be included for overall feedback
      ...(periodTab !== "custom" && { startdate, enddate }),
    };

    const payload = createSingleEventFeedbackPayload(payloadData);

    dotClickFeedback.connectWebSocket({
      url,
      payload,
      shouldConnect: () => {
        const shouldConnect =
          !dotClickFeedback.isConnected && !dotClickFeedback.loading;
        return shouldConnect;
      },
      onError: () => {
        console.error("WebSocket error in dot click feedback generation");
        setIsAIGenerating(false); // Clear global loading state on error
      },
             onDataReceived: (cleanedData) => {
         // Filter out status messages for dot click AI feedback
         const isStatusMessage = 
           cleanedData?.includes("Starting AI feedback generation") ||
           cleanedData?.includes("Analyzing performance data") ||
           cleanedData?.includes("Generating AI feedback") ||
           cleanedData?.includes("Heartbeat connection alive") ||
           cleanedData?.includes("message");
         
         if (isStatusMessage) {
           return;
         }
         
         setDotClickFeedbackText(cleanedData);
         setStudentCourse(true);
       },
       onComplete: async (finalData) => {
         // Filter out status messages for dot click AI feedback
         const isStatusMessage = 
           finalData?.includes("Starting AI feedback generation") ||
           finalData?.includes("Analyzing performance data") ||
           finalData?.includes("Generating AI feedback") ||
           finalData?.includes("Heartbeat connection alive") ||
           finalData?.includes("message");
         
         if (isStatusMessage) {
           setIsAIGenerating(false); // Clear global loading state
           return;
         }
         
         if (finalData && finalData.trim() !== "" && finalData !== "No student performance data available for analysis") {
           setDotClickFeedbackText(finalData);
           setStudentCourse(true);
         }
         
         setIsAIGenerating(false); // Clear global loading state
       },
    });
    }
    // Calculate daily_date from dot's data
    setTimeout(() => {
     generateDotClickFeedback(dot);
    }, 100); 
  };
  

  // Effect handlers
       useEffect(() => {
    if (value === "2") {
      // Only clear feedback text if we're switching to a different mode
      // Don't clear it if we already have feedback content
      if (!eventOverviewFeedbackText) {
        setEventOverviewFeedbackText("");
      }
    } else {
      setShowInsights(false);
      setdot("");
      setStudentCourse(false);
    }

        // Check if tab or period has changed
    const tabChanged = value !== previousValueRef.current;
    const periodChanged =
      JSON.stringify({
        periodTab,
        year,
        month,
        startdate,
        enddate,
        month_year,
      }) !== JSON.stringify(previousPeriodRef.current);

    // Check if checkboxes have changed
    const courseDataChanged = JSON.stringify(selectedCourseData) !== JSON.stringify(previousPeriodRef.current?.selectedCourseData);
    const studentDataChanged = JSON.stringify(selectedStudentData) !== JSON.stringify(previousPeriodRef.current?.selectedStudentData);
    const selectStudentChanged = JSON.stringify(selectstudent) !== JSON.stringify(previousPeriodRef.current?.selectstudent);
    
    const checkboxChanged = courseDataChanged || studentDataChanged || selectStudentChanged;

            // Clear all feedback states when tab, period, or checkboxes change
     if (tabChanged || periodChanged || checkboxChanged) {
       
               // Clear any existing feedback when tab, period, or checkboxes change
        setOverallFeedbackText("");
        setEventOverviewFeedbackText("");
        setHasOverallFeedbackData(false);
        setShowInsights(false);
        setStudentsAreaOfImprovement("");
        setPracticeAssignmentText("");
        setCourseAssignmentText("");
        setTestPrepText("");
        setAssignmentTypeStruggled("");
        setDotClickFeedbackText("");
        setStudentCourse(false);
        setDoteventStudent(false);
        setCourseFeedbackText("");
        setCourseFeedbackData({});
        setCourseFeedbackContent({});
        setCourseLoadingStates({});
        
        // Immediately clear loading states when tab, period, or checkboxes change
        setIsReceivingFeedback(false);
        setCurrentFeedbackType("");
        
        // Clear existing feedback state to always show "Generate with AI" button
        clearExistingFeedback();

        dispatch(resetPointData());
       
       handleTabSwitch(value);
       previousValueRef.current = value;
       previousPeriodRef.current = {
         periodTab,
         year,
         month,
         startdate,
         enddate,
         month_year,
         selectedCourseData,
         selectedStudentData,
         selectstudent,
       };
     }
  }, [
    value,
    periodTab,
    year,
    month,
    startdate,
    enddate,
    month_year,
    selectedCourseData,
    selectedStudentData,
    selectstudent,
    handleTabSwitch,
  ]);

  // Force re-render when dot data changes to update header immediately
  useEffect(() => {
    // This effect will trigger a re-render when dot data changes
    // ensuring the header updates immediately
    setHeaderUpdateTrigger(prev => prev + 1);
  }, [dot, dotClickedData, value, periodTab, year, month, startdate, enddate, month_year]);

  // Additional effect to ensure header updates when tab changes
  useEffect(() => {
    setHeaderUpdateTrigger(prev => prev + 1);
  }, [value]);

  // Effect to ensure header updates when period changes
  useEffect(() => {
    setHeaderUpdateTrigger(prev => prev + 1);
  }, [periodTab, year, month, startdate, enddate, month_year]);

  useEffect(() => {
    // Reset all states when dependencies change
    const resetAllStates = () => {
      // Check if any WebSocket is currently receiving data
      const isAnyWebSocketActive = 
        overallFeedback.isConnected || 
        courseFeedback.isConnected || 
        studentFeedback.isConnected || 
        dotClickFeedback.isConnected || 
        eventOverviewFeedback.isConnected ||
        isReceivingFeedback;

        if (isAnyWebSocketActive) {
        return;
      }

       setIsReceivingFeedback(false);
       resetTabSwitchState();
       setCourseFeedbackText("");
       setCourseFeedbackData({});
       setCourseFeedbackContent({});
       setStudentsAreaOfImprovement("");
       setAssignmentTypeStruggled("");
       setImpactOfPracticeAssignments("");
       setImpactOfAIAssistant("");
       setShowInsights(false);
       setStudentCourse(false);
       setDoteventStudent(false);
       setCourseLoadingStates({});
       setPracticeAssignmentText("");
       setCourseAssignmentText("");
       setTestPrepText("");
       setdot("");
       setCurrentFeedbackType("");
       setHasOverallFeedbackData(false);
       setOverallFeedbackText("");
       setEventOverviewFeedbackText("");
       setDotClickFeedbackText("");
       dispatch(resetCourseList());

       // Clear existing feedback state to always show "Generate with AI" button
       clearExistingFeedback();

       // Reset all WebSocket instances and their loading states
       overallFeedback.resetState();
       courseFeedback.resetState();
       studentFeedback.resetState();
       dotClickFeedback.resetState();
       eventOverviewFeedback.resetState();
       
       // Force reset loading states to ensure they're false
       setTimeout(() => {
         if (overallFeedback.loading) overallFeedback.resetState();
         if (courseFeedback.loading) courseFeedback.resetState();
         if (studentFeedback.loading) studentFeedback.resetState();
         if (dotClickFeedback.loading) dotClickFeedback.resetState();
         if (eventOverviewFeedback.loading) eventOverviewFeedback.resetState();
       }, 100);
    };

    resetAllStates();
  }, [
    periodTab,
    value,
    year,
    month,
    startdate,
    enddate,
    month_year,
    selectedCourseData,
    selectedStudentData,
    selectstudent,
    resetTabSwitchState,
  ]);

  useEffect(() => {
    if (value === "2") {
      // Check if any WebSocket is currently receiving data
      const isAnyWebSocketActive = 
        overallFeedback.isConnected || 
        courseFeedback.isConnected || 
        dotClickFeedback.isConnected ||
        isReceivingFeedback;

      if (isAnyWebSocketActive) {
        return;
      }

      setIsReceivingFeedback(false);
      resetTabSwitchState();
      setPracticeAssignmentText("");
      setCourseAssignmentText("");
      setTestPrepText("");
      setStudentCourse(false);
      setShowInsights(false);
      setCourseFeedbackContent({});
      setCourseFeedbackData({});
      setCourseLoadingStates({});

      // Clear existing feedback state to always show "Generate with AI" button
      clearExistingFeedback();

      // Reset WebSocket states
      overallFeedback.resetState();
      courseFeedback.resetState();
      dotClickFeedback.resetState();
    }
  }, [dot, resetTabSwitchState, clearExistingFeedback]);

  useEffect(() => {
    // Reset all feedback states when dot changes (including when dot is cleared)
    if (dot && dot !== null && dot.score && !dot.isPlaceholder) {
      // Reset all feedback states when switching to a new dot
      setIsReceivingFeedback(false);
      setStudentsAreaOfImprovement("");
      setImpactOfAIAssistant("");
      setAssignmentTypeStruggled("");
      setImpactOfPracticeAssignments("");
      setPracticeAssignmentText("");
      setCourseAssignmentText("");
      setTestPrepText("");
      setDotClickFeedbackText(""); // Clear previous feedback when dot changes
      setStudentCourse(false); // Reset studentCourse state when dot changes
      setDoteventStudent(false);
      setShowInsights(false);
      setCourseFeedbackText("");
      setCourseFeedbackData({});
      setCourseFeedbackContent({});
      setCourseLoadingStates({});
      
      // Clear existing feedback state to always show "Generate with AI" button
      clearExistingFeedback();
      
      // Reset WebSocket states
      dotClickFeedback.resetState();
      courseFeedback.resetState();
      
      // Force header update when dot changes
      setHeaderUpdateTrigger(prev => prev + 1);
    } else if (dot === null || dot === "") {
      // Clear all feedback when dot is explicitly cleared
      setDotClickFeedbackText("");
      setStudentCourse(false);
      setDoteventStudent(false);
      setShowInsights(false);
      setCourseFeedbackText("");
      setCourseFeedbackData({});
      setCourseFeedbackContent({});
      setCourseLoadingStates({});
      setIsReceivingFeedback(false);
      
      // Clear existing feedback state to always show "Generate with AI" button
      clearExistingFeedback();
      
      // Reset WebSocket states
      dotClickFeedback.resetState();
      courseFeedback.resetState();
      
      // Force header update
      setHeaderUpdateTrigger(prev => prev + 1);
    }
  }, [dot, clearExistingFeedback]); // Only depend on dot changes

  useEffect(() => {
    setShowButton(!(studentCourse && !dotClickFeedback.loading));
  }, [studentCourse, dotClickFeedback.loading]);

  // Trigger header update when date-related props change
  useEffect(() => {
    setHeaderUpdateTrigger(prev => prev + 1);
  }, [periodTab, year, month, startdate, enddate, month_year]);

  // Helper functions for display
  const displayYear = () => {
    if (periodTab === "year") return year;
    if (periodTab === "month") return month_year;
    return year;
  };

  const formatDate = (date) => {
    if (!date) return "";
    const [day, month, year] = date.split("-");
    const normalizedDay = day.padStart(2, "0");
    const normalizedMonth = month.padStart(2, "0");
    const normalizedDate = `${normalizedDay}-${normalizedMonth}-${year}`;
    return dayjs(normalizedDate, "DD-MM-YYYY").format("DD MMM YYYY");
  };

  const displayOnDot = () => {
    // First, try to use the dot's own data if available
    if (dot && dot !== null) {
      if (dot.originalPeriod) {
        return dot.originalPeriod;
      } else if (dot.full_period) {
        return dot.full_period;
      } else if (dot.month) {
        return dot.month;
      } else if (dot.originalDate) {
        return formatDate(dot.originalDate);
      } else if (dot.submission_date) {
        return formatDate(dot.submission_date);
      } else if (dot.actualDate) {
        return dot.actualDate;
      } else if (dot.actualMonth && dot.actualYear) {
        return `${dot.actualMonth} ${dot.actualYear}`;
      }
    }
    
    // Fallback to filter applied data
    if (timePeriodFilterApplied === "year") {
      return filterApplied?.month_year;
    } else if (timePeriodFilterApplied === "month") {
      return formatDate(filterApplied?.week_start_date);
    } else if (timePeriodFilterApplied === "week") {
      return formatDate(dotClickedData?.filters_applied.daily_date);
    } else if (timePeriodFilterApplied === "custom") {
      return;
    }
    return year;
  };

  // Enhanced function to get current display value with immediate updates
  const getCurrentDisplayValue = () => {
    
    // PRIORITY 1: If we have an active dot click, show that specific data
    if (dot && dot !== null && !dot.isPlaceholder && Object.keys(dot).length > 0) {
      if (dot.originalDate) {
        const result = formatDate(dot.originalDate);
        return result;
      } else if (dot.submission_date) {
        const result = formatDate(dot.submission_date);
        return result;
      } else if (dot.month) {
        // For real data dots, prioritize month field which contains the actual date
        return dot.month;
      } else if (dot.originalPeriod) {
        return dot.originalPeriod;
      } else if (dot.full_period) {
        return dot.full_period;
      } else if (dot.actualDate) {
        return dot.actualDate;
      } else if (dot.actualMonth && dot.actualYear) {
        const result = `${dot.actualMonth} ${dot.actualYear}`;
        return result;
      }
    }
    
    // PRIORITY 2: Calendar selection takes precedence over any stale dot data
    // For monthly, weekly, and custom tabs, show calendar selection
    if (periodTab === "month" || periodTab === "week" || periodTab === "custom") {
      let result;
      if (periodTab === "month") {
        // For monthly tab, show month-year format from calendar
        result = month_year || `${getMonthName(parseInt(month, 10))}-${year || currentYear}`;
        return result;
      } else if (periodTab === "week") {
        // For weekly tab, prioritize month_year over startdate
        result = month_year || (startdate ? formatDate(startdate) : `${getMonthName(parseInt(month, 10))}-${year || currentYear}`);
        return result;
      } else if (periodTab === "custom") {
        // For custom tab, show date range from calendar
        if (startdate && enddate) {
          result = `${formatDate(startdate)} - ${formatDate(enddate)}`;
        } else if (startdate) {
          // If only startdate is available, use current date as enddate
          const currentDate = getCurrentDateFormatted();
          result = `${formatDate(startdate)} - ${formatDate(currentDate)}`;
        } else {
          result = year || currentYear;
        }
        return result;
      }
    }
    
    // PRIORITY 3: For yearly tab, show only year
    if (periodTab === "year") {
      const result = year || currentYear;
      return result;
    }
    
    // PRIORITY 4: For tab 1 (overall performance), show the selected period
    if (value === "1") {
      let result;
      if (periodTab === "year") {
        // For yearly tab, show only year
        result = year || currentYear;
      } else if (periodTab === "month") {
        // For monthly tab, show month-year format
        result = month_year || `${getMonthName(parseInt(month, 10))}-${year || currentYear}`;
      } else if (periodTab === "week") {
        // For weekly tab, prioritize month_year over startdate
        result = month_year || (startdate ? formatDate(startdate) : `${getMonthName(parseInt(month, 10))}-${year || currentYear}`);
      } else if (periodTab === "custom") {
        // For custom tab, show date range or start date
        if (startdate && enddate) {
          result = `${formatDate(startdate)} - ${formatDate(enddate)}`;
        } else if (startdate) {
          // If only startdate is available, use current date as enddate
          const currentDate = getCurrentDateFormatted();
          result = `${formatDate(startdate)} - ${formatDate(currentDate)}`;
        } else {
          result = year || currentYear;
        }
      } else {
        result = year || currentYear;
      }
      return result;
    }
    
    // PRIORITY 5: For tab 2 (events), show the selected period
    if (value === "2") {
      let result;
      if (periodTab === "year") {
        // For yearly tab, show only year
        result = year || currentYear;
      } else if (periodTab === "month") {
        // For monthly tab, show month-year format
        result = month_year || `${getMonthName(parseInt(month, 10))}-${year || currentYear}`;
      } else if (periodTab === "week") {
        // For weekly tab, prioritize month_year over startdate
        result = month_year || (startdate ? formatDate(startdate) : `${getMonthName(parseInt(month, 10))}-${year || currentYear}`);
      } else if (periodTab === "custom") {
        // For custom tab, show date range or start date
        if (startdate && enddate) {
          result = `${formatDate(startdate)} - ${formatDate(enddate)}`;
        } else if (startdate) {
          // If only startdate is available, use current date as enddate
          const currentDate = getCurrentDateFormatted();
          result = `${formatDate(startdate)} - ${formatDate(currentDate)}`;
        } else {
          result = year || currentYear;
        }
      } else {
        result = year || currentYear;
      }
      return result;
    }
    
    // FALLBACK: Default to current year
    const fallback = year || currentYear;
    return fallback;
  };

  // Enhanced function to get dynamic AI feedback title
  const getDynamicAIFeedbackTitle = () => {
    const displayValue = getCurrentDisplayValue();
        
    // Determine the context for the title
    let context = "";
    if (value === "1") {
      context = "Overall Performance";
    } else if (value === "2") {
      if (dot && dot !== null && dot.student_name && typeof dot.student_name === 'string') {
        context = "Student Performance";
      } else {
        context = "Events Overview";
      }
    }
    
    // Determine the period type
    let periodType = "";
    if (periodTab === "year") {
      periodType = "Yearly";
    } else if (periodTab === "month") {
      periodType = "Monthly";
    } else if (periodTab === "week") {
      periodType = "Weekly";
    } else if (periodTab === "custom") {
      periodType = "Custom Period";
    }
    
    // Build the title
    if (context && periodType) {
      return `AI Feedback – ${context} (${periodType}: ${displayValue})`;
    } else {
      return `AI Feedback – ${displayValue}`;
    }
  };

  const shouldShowErrorMessage =
    globalState?.role === "Teacher"
      ? selectedCourseData.length === 0 || selectedStudentData.length === 0
      : selectstudent.length === 0;

  // Helper function to validate if existing feedback matches current period
  const isExistingFeedbackValid = (feedback) => {
    if (!feedback) return false;
    
    return true; // Trust the API filtering
  };

  // Determine which loading state to use based on context
  const getCurrentLoadingState = () => {
    // If we're tab switching, don't show loading state
    if (isTabSwitching) {
      return false;
    }
    
    // Always return true if AI is generating globally
    if (isAIGenerating) {
      return true;
    }
    
    const loadingState = (() => {
      if (value === "1")
        return overallFeedback.loading || studentFeedback.loading;
      if (value === "2" && dot && dot !== null && dot.student_name && typeof dot.student_name === 'string') return dotClickFeedback.loading;
      if (value === "2" && (!dot || !dot.student_name || typeof dot.student_name !== 'string'))
        return eventOverviewFeedback.loading;
      return false;
    })();
    
    return loadingState;
  };

  const getCurrentConnectionState = () => {
    return (
      isAIGenerating ||
      overallFeedback.isConnected ||
      courseFeedback.isConnected ||
      studentFeedback.isConnected ||
      dotClickFeedback.isConnected ||
      eventOverviewFeedback.isConnected
    );
  };
  // Messages to display
 const messages = [
    "Thinking",
    "Searching your data",
    "Double-checking everything",
    "Analyzing the patterns",
    "Organizing the information",
     "Preparing your insights",
    "Getting everything ready",
    "Just a moment, we're almost done"
  ];

  return (
    <>
      <div className="ai-feedback-performance-section">
        <div className="ai-feedback-performance-wrapper">
          {/* Header is now handled by StandardizedAIFeedback component */}
          <div className="ai-feedback-performance-header" key={headerUpdateTrigger}>
          <h2>
          <img src={ai_feedback_logo} alt="AI Feedback" />
          {getDynamicAIFeedbackTitle()}
          </h2>
          </div>

          {/* Student Daily Feedback Card */}
          {value === "2" &&
            !doteventStudent &&
            dot && dot !== null && dot.student_name && typeof dot.student_name === 'string' &&
            !isTabSwitching && (
              <div className="ai-feedback-performance-Avg-student-Score-card">
                <div className="ai-feedback-performance-Avg-student-Score-card-header">
                  <h5>{dot.student_name}</h5>
                </div>
                <div className="ai-feedback-performance-Avg-student-Score-card-content">
                  <span>Course : {dot.course_name}</span> <br />
                  <span>
                    Scored {dot?.score}% in {dot?.assignment_name}
                  </span>
                </div>

                {showButton && (
                  <button
                    className="ai-feedback-performance-button"
                    onClick={handleDotClickAI}
                    disabled={
                      getCurrentConnectionState() || getCurrentLoadingState()
                    }
                  >
                    Generate AI Feedback
                  </button>
                )}

                                 {studentCourse && !getCurrentLoadingState() && (
                   <div className="ai-feedback-performance-content">
                                           <StandardizedAIFeedback
                        feedbackData={{
                          feedback: dotClickFeedbackText || studentsAreaOfImprovement
                        }}
                        year={getCurrentDisplayValue()}
                        title={getDynamicAIFeedbackTitle()}
                      />
                   </div>
                 )}

                {getCurrentLoadingState() && (
                  <>
                    {/* <LoadingComponent />
                    <p className="loading-text">
                      Loading data EOV - please wait...
                    </p> */}
                    <LoadingComponent />
                    <LoadingMessages messages={messages}/>
                  </>
                )}
              </div>
            )}

          {/* Loading state for dot click API */}
          {dot && dot !== null && !dot.isPlaceholder && Object.keys(dot).length > 0 && 
           dotClickedDataStatus !== 200 && !isTabSwitching && value !== "2" && (
            <div className="ai-feedback-performance-content">
              <LoadingComponent />
              <p className="loading-text">
                Loading performance data - please wait...
              </p>
               {/* <LoadingComponent />
                    <p className="loading-text">
                     {currentMessage}
                        <span class="loader__dot">.</span><span class="loader__dot">.</span><span class="loader__dot">.</span>
                    </p> */}
            </div>
          )}

          {/* Main Feedback Section */}
          {dotClickedDataStatus !== 200 && !isTabSwitching && !dot && (
            <>
                             {(() => {
                               return null;
                             })()}

                             {!getCurrentConnectionState() &&
                 !overallFeedbackText &&
                 !isReceivingFeedback &&
                 value === "1" &&
                 !getCurrentLoadingState() &&
                 !hasOverallFeedbackData && (
                  <div className="ai-feedback-performance-content">
                    {existingFeedback && isExistingFeedbackValid(existingFeedback) ? (
                      // Show existing feedback if available
                      <StandardizedAIFeedback
                        feedbackData={{
                          feedback: existingFeedback.ai_generated_feedback
                        }}
                        year={getCurrentDisplayValue()}
                        title={getDynamicAIFeedbackTitle()}
                      />
                    ) : (
                      // Show generate button if no existing feedback
                      <>
                        <div className="ai-feedback-performance-image">
                          <img src={ai_feedback_image} alt="AI Feedback" />
                        </div>
                        {shouldShowErrorMessage ? (
                          <div className="error-message-overviewperformence-dashboard generate-ai-feedback">
                            <p>Generate AI Feedback after data.</p>
                          </div>
                        ) : (
                          <button
                            className="ai-feedback-performance-button"
                            onClick={
                              role === "Student"
                                ? handleRefreshAIStudent
                                : handleRefreshAI
                            }
                            disabled={
                              getCurrentConnectionState() ||
                              getCurrentLoadingState()
                            }
                          >
                            Generate AI Feedback
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}

                

                             {(getCurrentLoadingState() || (isReceivingFeedback && currentFeedbackType === "overall")) &&
                 value === "1" &&
                 !overallFeedbackText &&
                 !hasOverallFeedbackData &&
                 !isTabSwitching && (
                   <>
                     {/* <LoadingComponent />
                     <p className="loading-text">
                       Loading data - please wait...
                     </p> */}
                   <LoadingComponent/>
                     <LoadingMessages  messages={messages}/>
                   </>
                 )}

                                                               {/* Display live overall feedback when received from WebSocket */}
                                {overallFeedbackText &&
                  value === "1" &&
                  currentFeedbackType === "overall" && (
                   <div className="ai-feedback-performance-content">
                                           <StandardizedAIFeedback
                        feedbackData={{
                          feedback: overallFeedbackText
                        }}
                        year={getCurrentDisplayValue()}
                        title={getDynamicAIFeedbackTitle()}
                      />
                   </div>
                 )}

                             {(() => {
                               return null;
                             })()}

                             {!showInsights &&
                 value === "2" &&
                 (!dot || !dot.student_name || typeof dot.student_name !== 'string') &&
                 !getCurrentLoadingState() &&
                 !isReceivingFeedback &&
                 !eventOverviewFeedbackText && (
                  <div className="ai-feedback-performance-content">
                    {existingFeedback && isExistingFeedbackValid(existingFeedback) ? (
                      // Show existing feedback if available
                      <StandardizedAIFeedback
                        feedbackData={{
                          feedback: existingFeedback.ai_generated_feedback
                        }}
                        year={getCurrentDisplayValue()}
                        title={getDynamicAIFeedbackTitle()}
                      />
                    ) : (
                      // Show generate button if no existing feedback
                      <>
                        <div className="ai-feedback-performance-image">
                          <img src={ai_feedback_image} alt="AI Feedback" />
                        </div>
                        {shouldShowErrorMessage ? (
                          <div className="error-message-overviewperformence-dashboard generate-ai-feedback">
                            <p>Generate AI Feedback after data.</p>
                          </div>
                        ) : (
                          <button
                            className="ai-feedback-performance-button"
                            onClick={handleRefreshAIOverallTeacherSide}
                            disabled={
                              getCurrentConnectionState() ||
                              getCurrentLoadingState()
                            }
                          >
                            Generate AI Feedback
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}

              {(getCurrentLoadingState() || (isReceivingFeedback && currentFeedbackType === "event_overview")) &&
                value === "2" &&
                (!dot || !dot.student_name || typeof dot.student_name !== 'string') &&
                !showInsights &&
                !eventOverviewFeedbackText &&
                !isTabSwitching && (
                  <>
                    {/* <LoadingComponent />
                    <p className="loading-text">
                      Loading data - please wait...
                    </p> */}
                    <LoadingComponent />
                     <LoadingMessages messages={messages}/>
                  </>
                )}

                                                                                                                                        {/* Display live event overview feedback when received from WebSocket */}
                  {eventOverviewFeedbackText && 
                   value === "2" && 
                   currentFeedbackType === "event_overview" &&
                   (!dot || !dot.student_name || typeof dot.student_name !== 'string') && (
                    <div className="ai-feedback-performance-content">
                                             <StandardizedAIFeedback
                         feedbackData={{
                           feedback: eventOverviewFeedbackText
                         }}
                         year={getCurrentDisplayValue()}
                         title={getDynamicAIFeedbackTitle()}
                       />
                    </div>
                  )}

              {/* Show no data message for overall feedback when no data is available */}
              {!isReceivingFeedback &&
                !studentCourse &&
                !showInsights &&
                currentFeedbackType === "overall" &&
                !hasOverallFeedbackData &&
                value === "1" && (
                  <div className="ai-feedback-performance-content">
                    <div className="ai-feedback-performance-image">
                      <img src={ai_feedback_image} alt="AI Feedback" />
                    </div>
                    <div className="error-message-overviewperformence-dashboard generate-ai-feedback">
                      <p>No student performance data available for analysis.</p>
                    </div>
                  </div>
                )}
            </>
          )}

          {/* Course Analytics Section */}
          {dotClickedDataStatus === 200 && !isTabSwitching && (
            <div className="ai-feedback-performance-Avg-student-Score-wrapper">
              {value === "1" && (
                <div className="ai-feedback-performance-Avg-student-Score-container">
                  <h3>Average Student Score - {dotClickedData?.avg_score}%</h3>
                  <div className="ai-feedback-performance-Avg-student-Score-event-wrapper">
                    {dotCLickedCourseInformation &&
                    dotCLickedCourseInformation.length > 0 ? (
                      dotCLickedCourseInformation.map(
                        (individualCourse, index) => (
                          <div
                            key={index}
                            className="ai-feedback-performance-Avg-student-Score-event-folder"
                          >
                            <h4>{individualCourse.course_name}</h4>
                            <button
                              className="ai-feedback-performance-Avg-student-Score-event-button"
                              onClick={() => {
                                if (isAIGenerating) return; // Prevent navigation during AI generation
                                setdot(null); // Clear dot data when transitioning to Events Overview
                                setValue("2");
                              }}
                              disabled={isAIGenerating}
                              style={{
                                opacity: isAIGenerating ? 0.5 : 1,
                                cursor: isAIGenerating ? 'not-allowed' : 'pointer'
                              }}
                            >
                              View Events{" "}
                              <img src={arrow_outward} alt="arrow_outward" />
                            </button>
                            <ul>
                              <li>Avg. Score: ~{individualCourse?.score}%</li>
                              <li>
                                {role === "Teacher"
                                  ? `Submissions Received: ${individualCourse?.submissions ??
                                      0}`
                                  : `Submissions: ${individualCourse?.submissions ??
                                      0}`}
                              </li>
                              <li>
                                Practice Assignments:{" "}
                                {individualCourse?.practice_assignments}
                              </li>
                              <li>
                                AI Assistants used:{" "}
                                {individualCourse?.ai_assistants_used}
                              </li>
                            </ul>

                            {!courseLoadingStates[
                              individualCourse?.course_id
                            ] &&
                              !courseFeedbackContent[
                                individualCourse?.course_id
                              ] && (
                                <button
                                  className="ai-feedback-performance-Avg-student-Score-generate-feedback-button"
                                  onClick={
                                    role === "Student"
                                      ? () =>
                                          handleCourseRefreshAIStudent(
                                            individualCourse
                                          )
                                      : () =>
                                          handleCourseRefreshAI(
                                            individualCourse
                                          )
                                  }
                                  disabled={isAIGenerating}
                                  style={{
                                    opacity: isAIGenerating ? 0.5 : 1,
                                    cursor: isAIGenerating ? 'not-allowed' : 'pointer'
                                  }}
                                >
                                  Generate AI Feedback
                                </button>
                              )}

                          {(courseLoadingStates[individualCourse?.course_id] ||
                            courseFeedbackContent[individualCourse?.course_id]) && (
                            <div className="ai-feedback-performance-content">
                              {courseLoadingStates[individualCourse?.course_id] ? (
                                <>
                                  <LoadingComponent />
                                  <LoadingMessages messages={messages} />
                                </>
                              ) : (
                                <StandardizedAIFeedback
                                  feedbackData={{
                                    feedback: courseFeedbackText,
                                    studentsAreaOfImprovement:
                                      courseFeedbackData[individualCourse?.course_id]?.studentsAreaOfImprovement,
                                    impactOfPracticeAssignments:
                                      courseFeedbackData[individualCourse?.course_id]?.impactOfPracticeAssignments,
                                    impactOfAIAssistant:
                                      courseFeedbackData[individualCourse?.course_id]?.impactOfAIAssistant,
                                    assignmentTypeStruggled:
                                      courseFeedbackData[individualCourse?.course_id]?.assignmentTypeStruggled,
                                    recommendations:
                                      courseFeedbackData[individualCourse?.course_id]?.recommendations,
                                  }}
                                  year={getCurrentDisplayValue()}
                                  title={`${getDynamicAIFeedbackTitle()} - ${individualCourse.course_name}`}
                                />
                              )}
                            </div>
                          )}
                          </div>
                        )
                      )
                                         ) : (
                       <p>No course data available.</p>
                     )}
                   </div>
                 </div>
               )}
            </div>
          )}

                     
        </div>
      </div>
    </>
  );
};

export default AIFeedbackPerformance;
